import React from "react";
import { Link } from "react-router-dom";
import { Phone, Check, CalendarClock, Wallet, FileSignature, ArrowRight } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { PageHero } from "./Services";
import { brand, financing, heroImage } from "../data/site";

const points = [
  { icon: Wallet, title: "Repairs and Replacements", text: "Financing can cover an emergency leak repair, a full re-roof, underlayment replacement or foam recoating." },
  { icon: CalendarClock, title: "Work Now, Pay Over Time", text: "A leak doesn't wait for the next monsoon. Financing lets the work happen on the roof's schedule instead of the budget's." },
  { icon: FileSignature, title: "Straight Answers First", text: "You get the written scope and price before any financing conversation, so you always know what the work itself costs." },
];

const faqs = [
  {
    q: "Do you offer financing?",
    a: "Yes — we offer financing through Enhancify. Enhancify sends your application to several lenders at once, so one application can come back with more than one offer. We'll walk you through what's available for your situation, plus any payment schedule we can set up directly in your contract.",
  },
  {
    q: "Does Pepla lend the money?",
    a: "No. Pepla Roofing is a contractor, not a lender. Financing is arranged through Enhancify and funded by third-party lenders — approval, rates and terms are theirs to set, not ours.",
  },
  {
    q: "Will applying affect my credit?",
    a: "That depends on the lender and the type of application. Enhancify and the lender will tell you whether it's a soft or hard credit check before you commit — ask first if it matters to you.",
  },
  {
    q: "What if I don't qualify?",
    a: "We'll still work with you on the scope. Sometimes the answer is phasing the work, repairing now and replacing later, or focusing on the areas that are actually failing.",
  },
  {
    q: "Is a deposit required?",
    a: "Deposits and payment schedules are set out in your written contract before work starts. Arizona law limits how much a residential contractor can collect up front, and we follow it.",
  },
];

const Financing = () => (
  <div>
    <SEO
      title="Roof Financing in Phoenix, AZ | Pepla Roofing"
      description="Financing options for roof repair and replacement across Maricopa County. Written scope and price first, then payment options. Call (602) 834-4707."
      jsonLd={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            name: "Financing",
            url: `${SITE_URL}/financing`,
            publisher: { "@id": `${SITE_URL}/#business` },
          },
          {
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      }}
    />

    <PageHero
      eyebrow="Payment Options"
      title="A New Roof Without the Huge Upfront Cost"
      subtitle="Financing options for roofing — so a roof that needs work doesn't wait."
      image={heroImage}
      imageAlt="Arizona home with a new roof by Pepla Roofing"
    />

    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {points.map((p) => (
            <div key={p.title} className="card-lift bg-card border border-ink/10 rounded-2xl p-7">
              <p.icon size={28} className="text-brand mb-4" />
              <h2 className="font-heading text-lg uppercase tracking-wide mb-2">{p.title}</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="kicker">How It Works</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Price First, Then Payment</h2>
            <div className="brand-line mb-6" />
            <ul className="space-y-4">
              {[
                "We inspect and give you a written scope with line-item pricing.",
                "You decide what work you actually want done.",
                "If you want to spread the cost, we walk through the options available for your project.",
                "Any lender application, approval and terms come from the lender, not from us.",
                "Work is scheduled once you've signed the contract you're comfortable with.",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={14} className="text-brand" />
                  </span>
                  <span className="leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>

            {financing.partnerName && (
              <div className="mt-9 bg-secondary border border-ink/10 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-5">
                {financing.partnerLogo && (
                  <img
                    loading="lazy"
                    decoding="async"
                    src={financing.partnerLogo}
                    alt={financing.partnerName}
                    width="440"
                    height="160"
                    className="h-9 w-auto object-contain self-start sm:self-center shrink-0"
                  />
                )}
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {financing.partnerNote ||
                    `We offer financing through ${financing.partnerName}. Approval, rates and terms are the lender's decision.`}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-9">
              {financing.applyUrl ? (
                <a
                  href={financing.applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
                >
                  Apply With {financing.partnerName || "Our Lending Partner"} <ArrowRight size={18} />
                </a>
              ) : null}
              <a
                href={`tel:${brand.phoneRaw}`}
                className="inline-flex items-center gap-2 border border-ink/20 hover:border-brand hover:text-brand font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone size={18} /> Talk Through Options · {brand.phone}
              </a>
            </div>

            <p className="text-muted-foreground text-xs leading-relaxed mt-8">
              {brand.name} is a roofing contractor, not a lender or a
              broker. Financing is arranged through {financing.partnerName || "third-party lenders"} and is subject to their credit approval, rates, terms and
              program availability. We don't guarantee approval or any particular payment amount. Deposits and payment
              schedules follow your written contract and Arizona contracting law.
            </p>
          </div>

          <div id="quote" className="section-dark text-white rounded-2xl p-8 md:p-10 shadow-xl">
            <h2 className="font-heading text-2xl uppercase tracking-wide mb-2">Get Your Written Price</h2>
            <p className="text-white/60 text-sm mb-6">
              Start with a free inspection and an honest scope. Payment options come after you know the number.
            </p>
            <LeadForm dark />
          </div>
        </div>
      </div>
    </section>

    <section className="py-24 bg-secondary">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <span className="kicker">Answers</span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Financing Questions</h2>
          <div className="brand-line mx-auto" />
        </div>
        <div>
          {faqs.map((f) => (
            <div key={f.q} className="py-6 border-b border-ink/10 first:border-t first:border-ink/10">
              <h3 className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/insurance-claims"
            className="inline-flex items-center gap-2 border border-ink/20 hover:border-brand hover:text-brand font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
          >
            Storm Damage & Insurance Help <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Financing;
